import Pubnub, {
  MessageEvent,
  PresenceEvent as PNPresenceEvent,
  StatusEvent,
} from "pubnub";
import { pubnubApp } from "@/pubnub";
import {
  ITransportAdapter,
  RealtimeListener,
  RealtimePayload,
  RealtimeStatus,
} from "./types";

export class PubNubTransportAdapter implements ITransportAdapter {
  private pubnub: Pubnub;
  private listeners: Set<RealtimeListener> = new Set();
  private status: RealtimeStatus = "disconnected";
  private pnListener: object | null = null;

  constructor(customPubNub?: Pubnub) {
    this.pubnub = customPubNub || pubnubApp;
    this._setupPubNubListener();
  }

  private _setupPubNubListener() {
    this.pnListener = {
      message: (event: MessageEvent) => {
        try {
          const payload =
            typeof event.message === "string"
              ? JSON.parse(event.message)
              : event.message;
          for (const listener of this.listeners) {
            listener.onMessage?.(payload as RealtimePayload, event.channel);
          }
        } catch (e) {
          console.error(
            "PubNubTransportAdapter: message parse error",
            e,
            event.message,
          );
        }
      },
      presence: (event: PNPresenceEvent) => {
        for (const listener of this.listeners) {
          listener.onPresence?.({
            action:
              event.action === "join"
                ? "join"
                : event.action === "leave"
                  ? "leave"
                  : "timeout",
            uuid: event.uuid,
            channel: event.channel,
            timestamp: event.timestamp,
          });
        }
      },
      status: (statusEvent: StatusEvent) => {
        let newStatus: RealtimeStatus = "connected";
        if (statusEvent.category === "PNConnectedCategory") {
          newStatus = "connected";
        } else if (
          statusEvent.category === "PNNetworkDownCategory" ||
          statusEvent.category === "PNNetworkIssuesCategory"
        ) {
          newStatus = "disconnected";
        } else if ((statusEvent as { error?: boolean }).error) {
          newStatus = "error";
        }
        this.status = newStatus;
        for (const listener of this.listeners) {
          listener.onStatus?.(newStatus);
        }
      },
    };
    this.pubnub.addListener(this.pnListener);
  }

  subscribe(channels: string[], withPresence: boolean = false): void {
    if (channels.length === 0) return;
    this.status = "connecting";
    this.pubnub.subscribe({ channels, withPresence });
  }

  unsubscribe(channels: string[]): void {
    if (channels.length === 0) return;
    this.pubnub.unsubscribe({ channels });
  }

  async publish(channel: string, payload: RealtimePayload): Promise<void> {
    await this.pubnub.publish({
      channel,
      message: typeof payload === "string" ? payload : JSON.stringify(payload),
    });
  }

  addListener(listener: RealtimeListener): void {
    this.listeners.add(listener);
  }

  removeListener(listener: RealtimeListener): void {
    this.listeners.delete(listener);
  }

  async getPresence(channel: string): Promise<string[]> {
    try {
      if (typeof this.pubnub?.hereNow !== "function") {
        return [];
      }
      const response = await this.pubnub.hereNow({ channels: [channel] });
      const channelData = response?.channels?.[channel];
      if (channelData) {
        return channelData.occupants.map((o) => o.uuid);
      }
      return [];
    } catch (e) {
      console.error("PubNubTransportAdapter: getPresence failed", e);
      return [];
    }
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  setIdentifier(id: string): void {
    if (id && typeof this.pubnub.setUUID === "function") {
      this.pubnub.setUUID(id);
    }
  }

  disconnect(): void {
    if (this.pnListener) {
      this.pubnub.removeListener(this.pnListener);
    }
    this.status = "disconnected";
  }
}
